<template>
  <Modal
    class="spaceModal"
    :isOpen="isOpen"
    :buttons="{
      column: false,
      data: [
        {
          label: space ? $t('app.campaign.settings.buttons.cancel') : $t('app.modals.spaceEdit.buttons.create.cancel'),
          class: 'secondary',
        },
        {
          label: space ? $t('app.campaign.settings.buttons.save') : $t('app.modals.spaceEdit.buttons.create.confirm'),
          class: 'green',
          disabled: state.space?.name?.length < 2 || state.space?.name?.length > 50,
        },
      ],
    }"
    :title="space?.name || $t('app.modals.spaceEdit.createTitle')"
    @close="close()"
    @callbackBtn1="close()"
    @callbackBtn2="save()"
  >
    <template #extra>
      <h4>{{ $t("app.modals.spaceEdit.title") }}</h4>
      <VForm :displayErrors="false" :callback="save">
        <template #form-body>
          <VField :label="$t('app.modals.spaceEdit.spaceName.label')" :placeholder="$t('app.modals.spaceEdit.spaceName.placeholder')" name="space-name" v-model="state.space.name" rules="required|max:50|min:2" :displayError="true" />
          <div class="input-wrapper">
            <label for="space-logo">{{ $t("app.modals.spaceEdit.spaceImg.label") }}</label>
            <Dropload v-model="state.space.image_file" @update:modelValue="(event) => previewImage(event)" />
            <small>{{ $t("app.modals.spaceEdit.spaceImg.info") }}</small>
            <transition name="fade" mode="out-in">
              <p class="bold logo-preview" v-if="space || state.space.image_file">
                {{ $t("app.modals.spaceEdit.spaceImg.preview") }}
                <img :src="imageSrc" ref="spaceImage" alt="Logo preview" />
              </p>
            </transition>
          </div>
        </template>
      </VForm>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { getImage, getRandomSpacePicture } from "@/composables/getImage.js";
import { useImageStore } from "@/stores/image.js";
import { useCreateSpace, useEditSpace } from "@/queries/space.query";

const toast = useToast();

const route = useRoute();

const { uploadImage } = useImageStore();

const { mutateAsync: createSpace } = useCreateSpace();
const { mutateAsync: editSpace } = useEditSpace();

const emits = defineEmits(["close", "created"]);

const spaceImage = ref(null);

const imageSrc = computed(() => {
  if (state.space) {
    if (state.space.image_file) return;

    if (state.space.image_id) {
      return getImage(state.space.image_id);
    }

    if (state.space.name) {
      return getRandomSpacePicture(state.space.name);
    }
  }

  return null;
});

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  space: {
    type: Object,
    required: false,
  },
  action: {
    type: String,
    required: true,
  },
});

const state = reactive({
  space: null,
});

function previewImage(image) {
  setTimeout(() => {
    spaceImage.value.src = URL.createObjectURL(image);
  }, 100);
}

async function save() {
  if (state.space.image_file) {
    const { image_id } = await uploadImage(route.params.companyId, "workspace_image", state.space.image_file);

    if (image_id) {
      state.space.image_id = image_id;
    } else {
      toast.error("Une erreur est survenue lors du téléchargement de l'image");
    }
  }

  if (props.action === "create") {
    await createSpace({
      organisationId: route.params.companyId as string,
      name: state.space.name,
      imageId: state.space.image_id,
    }, {
      onSuccess: (data) => {
        toast.success("Nouvel espace de travail créé avec succès");
        emits("created", data);
      },
      onError: () => {
        toast.error("Un problème est survenus durant la création de l'espace de travail, veuillez ressayer plus tard.");
      },
    });
  } else {
    await editSpace({
      organisationId: route.params.companyId as string,
      name: state.space.name,
      imageId: state.space.image_id,
      spaceId: props.space.workspace_id,
    }, {
      onSuccess: () => {
        toast.success("Espace de travail modifié avec succès");
      },
      onError: () => {
        toast.error("Un problème est survenus durant la modification de l'espace de travail, veuillez ressayer plus tard.");
      },
    });
  }

  close();
}

function close() {
  state.space = null;
  emits("close");
}

watch(props, () => {
  state.space = { ...props.space, name: props.space?.name, image_file: null };
});
</script>
